import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useMemo, useState } from 'react';

import { AddCircle, Check, Close, Edit, Groups, Hexagon, PlayArrow, Security, Visibility, VisibilityOff, VpnKey } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton } from '@@components';

import {
	ClientClient,
	ClientDependencyModel,
	ClientModel,
	ComponentModel,
	ContextClient,
	ContextScopeModel,
	EnvironmentClientModel,
	EnvironmentModel,
	OperationsClient,
	SetClientDependenciesRequest,
	UpdateClientRequest,
} from '../../../api/api';
import { sortByName } from '../../../common/sortByName';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import { EnvironmentClientAttributeKeys, environmentClientAttributeRows, knownEnvironmentClientAttributeKeys } from '../EnvironmentClientAttributes';
import AccessCredentialHelp from './AccessCredentialHelp';
import AddClientDependencyDialog from './AddClientDependencyDialog';
import AttributesList from './AttributesList';
import { CLIENTS_PANEL_COLLAPSED_WIDTH, CLIENTS_PANEL_DEFAULT_WIDTH } from './ClientsPanel';
import DependencyTree from './DependencyTree';
import EnvironmentTabs from './EnvironmentTabs';
import RedirectUrlPlaceholderHint from './RedirectUrlPlaceholderHint';
import SectionWithAction from './SectionWithAction';

const CLIENT_DETAILS_PANEL_STORAGE_KEY = 'clientDetailsPanel.width';
const CLIENT_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const CLIENT_DETAILS_PANEL_MIN_WIDTH = 360;
const CLIENT_DETAILS_PANEL_MAX_WIDTH = 1000;
const OAUTH_CLIENT_CREDENTIALS_GRANT_TYPE = 'client_credentials';
const COMPONENT_APPLICATION_ID_ATTRIBUTE = 'Azure:ApplicationId';

interface ClientDetailsPanelProps {
	clientId: string;
	environmentId?: string;
	environment?: EnvironmentModel | null;
	environments?: EnvironmentModel[];
	onRotateEnvironmentSecret?: (environment: EnvironmentModel, definition: ClientModel) => void;
	environmentClient?: EnvironmentClientModel | null;
	components?: ComponentModel[];
	onClose: () => void;
	onRefresh?: () => void;
	onRotateSecret?: () => void;
	listPanelCollapsed?: boolean;
	rightOffset?: number;
	top?: number;
}

export default function ClientDetailsPanel({
	clientId,
	environmentId,
	environment: initialEnvironment,
	environments = [],
	onRotateEnvironmentSecret,
	environmentClient: initialDeployment,
	components = [],
	onClose,
	onRefresh,
	onRotateSecret,
	listPanelCollapsed = false,
	rightOffset = CLIENTS_PANEL_DEFAULT_WIDTH,
	top = 0,
}: ClientDetailsPanelProps) {
	const firstEnvironmentId = environments[0]?.id;
	const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(initialEnvironment?.id ?? environments[0]?.id);
	const availableEnvironments = environments.length ? environments : initialEnvironment ? [initialEnvironment] : [];
	const environment = availableEnvironments.find((item) => item.id === selectedEnvironmentId) ?? availableEnvironments[0];
	const environmentClient =
		environment?.clients?.find((item) => item.clientId === clientId) ??
		(!environments.length && environment?.id === initialEnvironment?.id ? initialDeployment : undefined);
	useEffect(() => {
		setSelectedEnvironmentId(initialEnvironment?.id ?? firstEnvironmentId);
	}, [initialEnvironment?.id, firstEnvironmentId]);
	useEffect(() => {
		setIsClientSecretVisible(false);
	}, [environment?.id]);
	const client = useApiClient(ClientClient) as ClientClient;
	const contextClient = useApiClient(ContextClient) as ContextClient;
	const operationsClient = useApiClient(OperationsClient) as OperationsClient;
	const toastService = useToastService();
	const [openAddDependencyDialog, setOpenAddDependencyDialog] = useState(false);
	const [addDependencyInitialComponentId, setAddDependencyInitialComponentId] = useState<string | undefined>();
	const [addScopesOnly, setAddScopesOnly] = useState(false);
	const [deletingDependencyId, setDeletingDependencyId] = useState<string | null>(null);
	const [isEditingName, setIsEditingName] = useState(false);
	const [nameDraft, setNameDraft] = useState('');
	const [isSavingName, setIsSavingName] = useState(false);
	const [isEditingRedirectUrls, setIsEditingRedirectUrls] = useState(false);
	const [redirectUrlDrafts, setRedirectUrlDrafts] = useState<string[]>(['']);
	const [isSavingRedirectUrls, setIsSavingRedirectUrls] = useState(false);
	const [isClientSecretVisible, setIsClientSecretVisible] = useState(false);
	const [panelWidth, setPanelWidth] = useState(CLIENT_DETAILS_PANEL_DEFAULT_WIDTH);

	const {
		data: clientDetails,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['client', clientId],
		queryFn: () => client.getById(clientId),
		enabled: !!clientId,
	});

	const dependencies = useMemo(() => clientDetails?.dependencies || [], [clientDetails?.dependencies]);
	const redirectUrls = useMemo(() => clientDetails?.redirectUrls ?? [], [clientDetails?.redirectUrls]);
	const deploymentAttributes = useMemo(() => environmentClient?.attributes || {}, [environmentClient?.attributes]);
	const oauthClientId = deploymentAttributes[EnvironmentClientAttributeKeys.clientId]?.toString() ?? '';
	const oauthClientSecret = deploymentAttributes[EnvironmentClientAttributeKeys.clientSecret]?.toString() ?? '';
	const oauthTokenEndpoint = deploymentAttributes[EnvironmentClientAttributeKeys.tokenEndpoint]?.toString() ?? '';
	const hasDeploymentAttributes = Object.keys(deploymentAttributes).length > 0;
	const additionalDeploymentAttributes = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(deploymentAttributes)
					.filter(([key, value]) => !knownEnvironmentClientAttributeKeys.has(key) && value !== undefined && value !== null && value !== '')
					.map(([key, value]) => [key, String(value)])
					.sort(([left], [right]) => left.localeCompare(right)),
			),
		[deploymentAttributes],
	);
	const knownDeploymentAttributes = useMemo(
		() =>
			Object.fromEntries(
				environmentClientAttributeRows
					.map((row) => [row.label, deploymentAttributes[row.key]] as const)
					.filter(([, value]) => value !== undefined && value !== null && value !== '')
					.map(([label, value]) => [label, String(value)]),
			),
		[deploymentAttributes],
	);

	const { data: dependencyMetadata } = useQuery({
		queryKey: [
			'client-dependency-metadata',
			dependencies.map((dependency) => `${dependency.dependsOnComponentId}:${dependency.targetType}:${dependency.targetId}`).join('|'),
		],
		queryFn: async () => {
			const componentIds = Array.from(new Set(dependencies.map((dependency) => dependency.dependsOnComponentId)));
			const metadata: ClientDependencyMetadata = {
				contextScopesByComponentId: {},
				operationNameById: {},
			};

			await Promise.all(
				componentIds.map(async (dependencyComponentId) => {
					const component = components.find((item) => item.id === dependencyComponentId);
					if (!component?.contextId) return;

					try {
						const [context, operations] = await Promise.all([
							contextClient.getContextById(component.contextId),
							operationsClient.getOperationsForContext(component.contextId),
						]);

						metadata.contextScopesByComponentId[dependencyComponentId] = context.scopes ?? [];
						operations.forEach((operation) => {
							metadata.operationNameById[operation.id] = operation.name;
						});
					} catch (error) {
						console.error(`Failed to load dependency metadata for component ${dependencyComponentId}:`, error);
					}
				}),
			);

			return metadata;
		},
		enabled: dependencies.length > 0 && components.length > 0,
	});

	const groupedDependencies = useMemo(
		() =>
			groupDependenciesByComponent(
				dependencies,
				components,
				dependencyMetadata ?? {
					contextScopesByComponentId: {},
					operationNameById: {},
				},
			),
		[components, dependencies, dependencyMetadata],
	);
	const oauthScopes = useMemo(() => {
		if (!environment) return [];

		return groupedDependencies
			.map((dependency) => {
				const environmentComponent = environment.components?.find((item) => item.componentId === dependency.componentId);
				const applicationId = environmentComponent?.attributes?.[COMPONENT_APPLICATION_ID_ATTRIBUTE]?.toString();
				if (!applicationId) return null;

				return {
					componentName: dependency.componentName,
					value: `api://${applicationId}/.default`,
				};
			})
			.filter((item): item is { componentName: string; value: string } => item !== null)
			.filter((item, index, items) => items.findIndex((candidate) => candidate.value === item.value) === index)
			.sort((left, right) => left.componentName.localeCompare(right.componentName));
	}, [environment, groupedDependencies]);

	useEffect(() => {
		if (!isEditingName) {
			setNameDraft(clientDetails?.name ?? '');
		}
	}, [clientDetails?.name, isEditingName]);

	useEffect(() => {
		if (!isEditingRedirectUrls) {
			setRedirectUrlDrafts(redirectUrls.length > 0 ? redirectUrls : ['']);
		}
	}, [isEditingRedirectUrls, redirectUrls]);

	const handleRefresh = async () => {
		await refetch();
		onRefresh?.();
	};

	const openAddPermissionsDialog = () => {
		setAddDependencyInitialComponentId(undefined);
		setAddScopesOnly(false);
		setOpenAddDependencyDialog(true);
	};

	const openAddScopesDialog = (componentId: string) => {
		setAddDependencyInitialComponentId(componentId);
		setAddScopesOnly(true);
		setOpenAddDependencyDialog(true);
	};

	const handleDeleteDependency = async (dependencyId: string, targetId: string) => {
		setDeletingDependencyId(dependencyId);
		try {
			const request: SetClientDependenciesRequest = {
				remove: [targetId],
			};
			await client.setDependencies(clientId, request);
			toastService.success('Client dependency removed successfully.');
			await handleRefresh();
		} catch (error) {
			console.error('Error removing client dependency:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove dependency: ${errorMessage}`);
		} finally {
			setDeletingDependencyId(null);
		}
	};

	const handleDeleteComponentDependencies = async (componentId: string, componentName: string, targetIds: string[]) => {
		if (targetIds.length === 0) return;

		setDeletingDependencyId(componentId);
		try {
			const request: SetClientDependenciesRequest = {
				remove: Array.from(new Set(targetIds)),
			};
			await client.setDependencies(clientId, request);
			toastService.success(`Removed all dependencies for ${componentName}.`);
			await handleRefresh();
		} catch (error) {
			console.error('Error removing client component dependencies:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove dependencies for ${componentName}: ${errorMessage}`);
		} finally {
			setDeletingDependencyId(null);
		}
	};

	const handleStartEditingName = () => {
		setNameDraft(clientDetails?.name ?? '');
		setIsEditingName(true);
	};

	const handleCancelEditingName = () => {
		setNameDraft(clientDetails?.name ?? '');
		setIsEditingName(false);
	};

	const handleSaveName = async () => {
		if (!clientDetails) return;

		const normalizedName = nameDraft.trim();
		if (!normalizedName) {
			toastService.error('Client name is required.');
			return;
		}

		const request: UpdateClientRequest = {
			name: normalizedName,
			description: clientDetails.description,
		};

		setIsSavingName(true);
		try {
			await client.update(clientId, request);
			toastService.success('Client name updated successfully.');
			setIsEditingName(false);
			await handleRefresh();
		} catch (error) {
			console.error('Error updating client name:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update client name: ${errorMessage}`);
		} finally {
			setIsSavingName(false);
		}
	};

	const handleStartEditingRedirectUrls = () => {
		setRedirectUrlDrafts(redirectUrls.length > 0 ? redirectUrls : ['']);
		setIsEditingRedirectUrls(true);
	};

	const handleCancelEditingRedirectUrls = () => {
		setRedirectUrlDrafts(redirectUrls.length > 0 ? redirectUrls : ['']);
		setIsEditingRedirectUrls(false);
	};

	const handleChangeRedirectUrlDraft = (index: number, value: string) => {
		setRedirectUrlDrafts((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
	};

	const handleAddRedirectUrlDraft = () => {
		setRedirectUrlDrafts((current) => [...current, '']);
	};

	const handleRemoveRedirectUrlDraft = (index: number) => {
		setRedirectUrlDrafts((current) => {
			const next = current.filter((_, itemIndex) => itemIndex !== index);
			return next.length > 0 ? next : [''];
		});
	};

	const handleSaveRedirectUrls = async () => {
		if (!clientDetails) return;

		const normalizedRedirectUrls = normalizeRedirectUrls(redirectUrlDrafts);
		const request: UpdateClientRequest = {
			description: clientDetails.description,
			redirectUrls: normalizedRedirectUrls,
		};

		setIsSavingRedirectUrls(true);
		try {
			await client.update(clientId, request);
			toastService.success('Redirect URI templates updated successfully.');
			setIsEditingRedirectUrls(false);
			await handleRefresh();
		} catch (error) {
			console.error('Error updating redirect URI templates:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update redirect URI templates: ${errorMessage}`);
		} finally {
			setIsSavingRedirectUrls(false);
		}
	};

	const renderOAuthSecretValue = () => {
		if (!oauthClientSecret) {
			return (
				<Typography variant="body2" color="textSecondary">
					Deploy this client to the environment to generate a secret.
				</Typography>
			);
		}

		return (
			<Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
				<Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', flexGrow: 1, minWidth: 0 }}>
					{isClientSecretVisible ? oauthClientSecret : '••••••••••••'}
				</Typography>
				<Tooltip title={isClientSecretVisible ? 'Hide client secret' : 'Show client secret'}>
					<IconButton size="small" onClick={() => setIsClientSecretVisible((current) => !current)} sx={{ p: 0.25, flexShrink: 0 }}>
						{isClientSecretVisible ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
					</IconButton>
				</Tooltip>
				<CopyToClipboardButton value={oauthClientSecret} size="small" iconSize={14} title="Copy client secret" edge={false} sx={{ p: 0.25, flexShrink: 0 }} />
			</Stack>
		);
	};

	const renderOAuthUsageRow = (label: string, value: string, copyTitle: string) => (
		<Stack direction="row" spacing={1} alignItems="center">
			<Typography variant="body2" color="textSecondary" sx={{ width: 96, flexShrink: 0 }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', flexGrow: 1, minWidth: 0 }}>
				{value || 'Not deployed'}
			</Typography>
			{value && <CopyToClipboardButton value={value} size="small" iconSize={14} title={copyTitle} edge={false} sx={{ p: 0.25, flexShrink: 0 }} />}
		</Stack>
	);

	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, flexShrink: 1 }}>
				ID: {clientId}
			</Typography>
			<CopyToClipboardButton value={clientId} size="small" iconSize={14} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
		</Box>
	);

	return (
		<>
			<SidePanelDetail
				title="API Client Details"
				subtitle={subtitle}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				errorMessage="Failed to load client details"
				onClose={onClose}
				onRefresh={handleRefresh}
				position="stacked"
				top={top}
				rightOffset={rightOffset}
				width={panelWidth}
				zIndex={1201}
				listPanelCollapsed={listPanelCollapsed}
				listPanelCollapsedWidth={CLIENTS_PANEL_COLLAPSED_WIDTH}
				resizeStorageKey={CLIENT_DETAILS_PANEL_STORAGE_KEY}
				defaultWidth={CLIENT_DETAILS_PANEL_DEFAULT_WIDTH}
				minWidth={CLIENT_DETAILS_PANEL_MIN_WIDTH}
				maxWidth={CLIENT_DETAILS_PANEL_MAX_WIDTH}
				setWidth={setPanelWidth}
			>
				{clientDetails && (
					<>
						<AccessCredentialHelp kind="client" />
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Name:
								</Typography>
								{isEditingName ? (
									<>
										<TextField
											value={nameDraft}
											onChange={(event) => setNameDraft(event.target.value)}
											size="small"
											fullWidth
											disabled={isSavingName}
											autoFocus
										/>
										<Tooltip title="Save client name">
											<span>
												<IconButton size="small" color="primary" onClick={handleSaveName} disabled={isSavingName || !nameDraft.trim()} sx={{ p: 0.5 }}>
													<Check sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Cancel">
											<span>
												<IconButton size="small" onClick={handleCancelEditingName} disabled={isSavingName} sx={{ p: 0.5 }}>
													<Close sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
									</>
								) : (
									<>
										<Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
											{clientDetails.name}
										</Typography>
										<CopyToClipboardButton value={clientDetails.name} size="small" iconSize={14} title="Copy name to clipboard" edge={false} />
										<Tooltip title="Edit client name">
											<IconButton size="small" onClick={handleStartEditingName} sx={{ p: 0.5 }}>
												<Edit sx={{ fontSize: 16 }} />
											</IconButton>
										</Tooltip>
									</>
								)}
							</Stack>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Code:
								</Typography>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, flexGrow: 1 }}>
									{clientDetails.code}
								</Typography>
								<CopyToClipboardButton value={clientDetails.code} size="small" iconSize={14} title="Copy code to clipboard" edge={false} />
							</Stack>
							{clientDetails.description && (
								<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
									{clientDetails.description}
								</Typography>
							)}
						</Box>

						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
								<Tooltip title="Resolved during environment client deployment">
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
										Redirect URI Templates
									</Typography>
								</Tooltip>
								<Box sx={{ flexGrow: 1 }} />
								{isEditingRedirectUrls ? (
									<>
										<Tooltip title="Save redirect URI templates">
											<span>
												<IconButton size="small" color="primary" onClick={handleSaveRedirectUrls} disabled={isSavingRedirectUrls} sx={{ p: 0.5 }}>
													<Check sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Cancel">
											<span>
												<IconButton size="small" onClick={handleCancelEditingRedirectUrls} disabled={isSavingRedirectUrls} sx={{ p: 0.5 }}>
													<Close sx={{ fontSize: 16 }} />
												</IconButton>
											</span>
										</Tooltip>
									</>
								) : (
									<Tooltip title="Edit redirect URI templates">
										<IconButton size="small" onClick={handleStartEditingRedirectUrls} sx={{ p: 0.5 }}>
											<Edit sx={{ fontSize: 16 }} />
										</IconButton>
									</Tooltip>
								)}
							</Stack>

							{isEditingRedirectUrls ? (
								<Stack spacing={1}>
									{redirectUrlDrafts.map((redirectUrl, index) => (
										<Stack key={index} direction="row" spacing={1} alignItems="center">
											<TextField
												value={redirectUrl}
												onChange={(event) => handleChangeRedirectUrlDraft(index, event.target.value)}
												placeholder="https://{HostName}/callback"
												size="small"
												fullWidth
												disabled={isSavingRedirectUrls}
											/>
											<IconButton size="small" onClick={() => handleRemoveRedirectUrlDraft(index)} disabled={isSavingRedirectUrls} sx={{ p: 0.5 }}>
												<Close sx={{ fontSize: 16 }} />
											</IconButton>
										</Stack>
									))}
									<Button
										size="small"
										variant="outlined"
										startIcon={<AddCircle />}
										onClick={handleAddRedirectUrlDraft}
										disabled={isSavingRedirectUrls}
										sx={{ alignSelf: 'flex-start' }}
									>
										Add URI
									</Button>
								</Stack>
							) : redirectUrls.length > 0 ? (
								<Stack spacing={0.75}>
									{redirectUrls.map((redirectUrl) => (
										<Typography key={redirectUrl} variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
											{redirectUrl}
										</Typography>
									))}
								</Stack>
							) : (
								<Typography variant="body2" color="textSecondary">
									No redirect URI templates configured.
								</Typography>
							)}
							<Box sx={{ mt: 1 }}>
								<RedirectUrlPlaceholderHint clientId={clientId} environmentId={environment?.id ?? environmentId} />
							</Box>
						</Box>

						<SectionWithAction
							title="Component Dependencies"
							count={dependencies.length}
							onAdd={openAddPermissionsDialog}
							addButtonTitle="Add component permissions"
							emptyMessage="No component permissions configured"
						>
							<DependencyTree
								root={{
									id: clientDetails.id,
									label: clientDetails.name,
									icon: <Groups sx={{ fontSize: 16, color: 'primary.main' }} />,
									children: groupedDependencies.map((group) => ({
										id: group.componentId,
										label: group.componentName,
										icon: <Hexagon sx={{ fontSize: 14, color: 'primary.main' }} />,
										action: createRemoveComponentDependenciesAction(group),
										children: [
											{
												id: `${group.componentId}-scopes`,
												label: 'Scopes',
												icon: <Security sx={{ fontSize: 14, color: 'primary.main' }} />,
												action: createAddScopesAction(group.componentId, group.componentName),
												children: group.scopes.map((dependency) => ({
													id: dependency.id,
													label: (
														<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
															<Typography variant="body2" noWrap color={dependency.isMissing ? 'warning.main' : undefined}>
																{dependency.name}
															</Typography>
															{dependency.isMissing && <Chip label="Missing" color="warning" size="small" variant="outlined" sx={{ height: 18 }} />}
														</Stack>
													),
													icon: <Security sx={{ fontSize: 14, color: dependency.isMissing ? 'warning.main' : 'primary.main' }} />,
													action: createRemoveDependencyAction(dependency.id, dependency.targetId),
												})),
											},
											...(group.operations.length > 0
												? [
														{
															id: `${group.componentId}-operations`,
															label: 'Operations',
															icon: <PlayArrow sx={{ fontSize: 14, color: 'primary.main' }} />,
															children: group.operations.map((dependency) => ({
																id: dependency.id,
																label: (
																	<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
																		<Typography variant="body2" noWrap>
																			{dependency.name}
																		</Typography>
																		<Chip label="Operation" size="small" variant="outlined" sx={{ height: 18 }} />
																	</Stack>
																),
																icon: <PlayArrow sx={{ fontSize: 14, color: 'primary.main' }} />,
																action: createRemoveDependencyAction(dependency.id, dependency.targetId),
															})),
														},
													]
												: []),
										],
									})),
								}}
							/>
						</SectionWithAction>

						<Divider sx={{ my: 2 }} />

						<EnvironmentTabs
							environments={availableEnvironments}
							environmentId={environment?.id}
							disabled={isSavingName || isSavingRedirectUrls}
							label="API Client environments"
							onChange={setSelectedEnvironmentId}
						/>
						{!environmentClient && (
							<Alert severity="info">
								{environment ? 'Deploy this API client from the environment node to see its credentials.' : 'No environments available.'}
							</Alert>
						)}

						{environmentClient && (
							<>
								<Stack spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
										Actions
									</Typography>
									{(onRotateEnvironmentSecret || onRotateSecret) && (
										<Button
											variant="outlined"
											size="small"
											startIcon={<VpnKey />}
											onClick={() => {
												if (environment && onRotateEnvironmentSecret) {
													onRotateEnvironmentSecret(environment, clientDetails);
												} else {
													onRotateSecret?.();
												}
											}}
										>
											Rotate Secret
										</Button>
									)}
								</Stack>

								<Box
									sx={{
										mb: 2,
										px: 1.5,
										py: 1.25,
										border: 1,
										borderColor: 'divider',
										borderRadius: 1,
										backgroundColor: 'action.hover',
									}}
								>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
										OAuth2 Client Credentials Usage
									</Typography>
									<Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
										Request a token from the token endpoint using these form values.
									</Typography>
									<Stack spacing={1}>
										{renderOAuthUsageRow('Token URL', oauthTokenEndpoint, 'Copy token endpoint')}
										{renderOAuthUsageRow('Grant Type', OAUTH_CLIENT_CREDENTIALS_GRANT_TYPE, 'Copy grant type')}
										{renderOAuthUsageRow('Client ID', oauthClientId, 'Copy client ID')}
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="body2" color="textSecondary" sx={{ width: 96, flexShrink: 0 }}>
												Client Secret
											</Typography>
											{renderOAuthSecretValue()}
										</Stack>
										{oauthScopes.length > 0 ? (
											oauthScopes.map((scope) => (
												<Stack key={scope.value} direction="row" spacing={1} alignItems="center">
													<Typography variant="body2" color="textSecondary" sx={{ width: 144, flexShrink: 0 }}>
														Scope for {scope.componentName}
													</Typography>
													<Stack sx={{ flexGrow: 1, minWidth: 0 }}>
														<Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
															{scope.value}
														</Typography>
													</Stack>
													<CopyToClipboardButton
														value={scope.value}
														size="small"
														iconSize={14}
														title="Copy scope"
														edge={false}
														sx={{ p: 0.25, flexShrink: 0 }}
													/>
												</Stack>
											))
										) : (
											<Typography variant="body2" color="textSecondary">
												Add a component dependency and deploy the target component to show its `.default` scope.
											</Typography>
										)}
									</Stack>
								</Box>

								<AttributesList
									attributes={{ ...knownDeploymentAttributes, ...additionalDeploymentAttributes }}
									title="Deployment Attributes"
									emptyMessage="Credentials will appear after the client deployment release plan completes."
								/>

								{!hasDeploymentAttributes && <Divider sx={{ my: 2 }} />}
							</>
						)}

						{environmentClient && hasDeploymentAttributes && <Divider sx={{ my: 2 }} />}

						<AttributesList attributes={clientDetails.attributes} title="Client Attributes" emptyMessage="No attributes defined for this client" />
					</>
				)}
			</SidePanelDetail>

			<AddClientDependencyDialog
				open={openAddDependencyDialog}
				clientId={clientId}
				components={components}
				existingDependencies={dependencies}
				initialComponentId={addDependencyInitialComponentId}
				scopeOnly={addScopesOnly}
				onClose={() => setOpenAddDependencyDialog(false)}
				onDependencyAdded={() => {
					setOpenAddDependencyDialog(false);
					handleRefresh();
				}}
			/>
		</>
	);

	function createRemoveDependencyAction(dependencyId: string, targetId: string) {
		return (
			<Tooltip title="Remove dependency">
				<span>
					<IconButton
						size="small"
						onClick={() => handleDeleteDependency(dependencyId, targetId)}
						disabled={deletingDependencyId === dependencyId}
						color="error"
					>
						<Close fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		);
	}

	function createAddScopesAction(componentId: string, componentName: string) {
		return (
			<Tooltip title={`Add scopes for ${componentName}`}>
				<IconButton size="small" onClick={() => openAddScopesDialog(componentId)} color="primary" sx={{ p: 0.25 }}>
					<AddCircle fontSize="small" />
				</IconButton>
			</Tooltip>
		);
	}

	function createRemoveComponentDependenciesAction(group: GroupedDependency) {
		return (
			<Tooltip title={`Remove all dependencies for ${group.componentName}`}>
				<span>
					<IconButton
						size="small"
						onClick={() => handleDeleteComponentDependencies(group.componentId, group.componentName, group.targetIds)}
						disabled={deletingDependencyId === group.componentId}
						color="error"
						sx={{ p: 0.25 }}
					>
						<Close fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		);
	}
}

interface ClientDependencyMetadata {
	contextScopesByComponentId: Record<string, ContextScopeModel[]>;
	operationNameById: Record<string, string>;
}

interface GroupedDependencyItem {
	id: string;
	targetId: string;
	name: string;
	isMissing?: boolean;
}

interface GroupedDependency {
	componentId: string;
	name: string;
	componentName: string;
	targetIds: string[];
	scopes: GroupedDependencyItem[];
	operations: GroupedDependencyItem[];
}

function groupDependenciesByComponent(
	dependencies: ClientDependencyModel[],
	components: ComponentModel[],
	metadata: ClientDependencyMetadata,
): GroupedDependency[] {
	const grouped = new Map<string, GroupedDependency>();

	dependencies.forEach((dependency) => {
		const component = components.find((item) => item.id === dependency.dependsOnComponentId);
		const group =
			grouped.get(dependency.dependsOnComponentId) ??
			({
				componentId: dependency.dependsOnComponentId,
				name: component?.name || dependency.dependsOnComponentId,
				componentName: component?.name || dependency.dependsOnComponentId,
				targetIds: [],
				scopes: [],
				operations: [],
			} satisfies GroupedDependency);

		group.targetIds.push(dependency.targetId);

		if (dependency.targetType === 'Scope') {
			const scope = metadata.contextScopesByComponentId[dependency.dependsOnComponentId]?.find((item) => item.id === dependency.targetId);
			group.scopes.push({
				id: dependency.id || dependency.targetId,
				targetId: dependency.targetId,
				name: scope?.name || dependency.targetId,
				isMissing: !scope,
			});
		} else if (dependency.targetType === 'Operation') {
			group.operations.push({
				id: dependency.id || dependency.targetId,
				targetId: dependency.targetId,
				name: metadata.operationNameById[dependency.targetId] || dependency.targetId,
			});
		}

		grouped.set(dependency.dependsOnComponentId, group);
	});

	return Array.from(grouped.values())
		.map((group) => ({
			...group,
			targetIds: Array.from(new Set(group.targetIds)),
			scopes: group.scopes.sort(sortByName),
			operations: group.operations.sort(sortByName),
		}))
		.sort(sortByName);
}

function normalizeRedirectUrls(redirectUrls: string[]): string[] {
	return Array.from(new Set(redirectUrls.map((item) => item.trim()).filter(Boolean))).sort((left, right) => left.localeCompare(right));
}
